import { memo, useContext, useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import { useQueryClient } from '@zg-rentals/react-query5';

import { Box } from '@zillow/constellation-10';

import { DashboardContext } from '../../containers/DashboardPage/context';
import { ModularListingContext } from '../../containers/CreateListingPage/context';
import { PaymentGatewayContext } from '../PaymentGateway/context';

import { useSelector } from '../../selectors/useSelector';

import { useGetNextStepsSummary } from '../../hooks/queries/summary';

import actions from '../../containers/CreateListingPage/actions';

import { default as pgActions } from '../PaymentGateway/actions';

import PropertiesActions from '../../actions/PropertiesActions';

import { DISPLAY_CONTEXT } from '../PaymentGateway/constants';
import PropertyConstants from '../../constants/PropertyConstants';
import { TRIGGER_LOCATIONS } from '../../constants/ClickstreamConstants';

import { RMW_PATH } from '../../pathsManifest';

import NextStepsRecommendationsCSEvents from '../../model/ClickstreamEvent/NextStepsRecommendationsCSEvents';
import ApplicationsCSEvents from '../../model/ClickstreamEvent/ApplicationsCSEvents';

import ConnectedModuleCard from '../ConnectedModuleCard';
import SimpleCard from '../SimpleCard';
import ErrorMessagesAlert from '../ErrorMessagesAlert';

import ApplicationsModalWrapper from './components/ApplicationsModalWrapper';
import Section from './components/Section';

import type { State as DashboardState } from '../../containers/DashboardPage/reducer';
import type { TileType } from '../../model/NextSteps';

const TILE_ID = {
  ENABLE_APPLICATIONS: 'enableApplications',
  RELIST: 'relist',
  RENTAL_PROTECTION: 'rentalProtection',
  UPGRADE_TO_PREMIUM: 'upgradeToPremium',
  LOADING: 'loading',
  TOURING_AVAILABILITY: 'touringAvailability',
};

const MOCK_SECTION = {
  id: 'loadingSection',
  title: '',
  tiles: [
    {
      id: TILE_ID.LOADING,
      title: ' ',
      icon: ' ',
      url: '',
    },
  ],
};

const NextStepsModule = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { dashboardState } = useContext(DashboardContext);
  const { modularListingState, reducerDispatch } = useContext(ModularListingContext);

  const {
    pgState: { isResponseSuccess },
    reducerDispatch: pgReducerDispatch,
  } = useContext(PaymentGatewayContext);

  const {
    hasFieldError,
    serverErrorObj = null,
    isSaveComplete = false,
  }: {
    hasFieldError?: boolean;
    serverErrorObj?: { fieldName: string } | null;
    isSaveComplete?: boolean;
  } = modularListingState;

  const { isRentalProtectionEligible, listingAlias, currentProperty, user } = useSelector((state) => {
    return {
      isRentalProtectionEligible: state.properties.currentProperty.rentalProtection.isEligible,
      listingAlias: state.properties.currentProperty.listingAlias,
      currentProperty: state.properties.currentProperty,
      user: state.user,
    };
  }, shallowEqual);

  const [displayApplicationsModal, setDisplayApplicationsModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const initialRender = useRef(true);

  const { activePath: activeDashboardPath }: Partial<DashboardState> = dashboardState;

  const {
    data,
    isPending,
    isFetching,
    isStale,
    error,
    refetch: refetchGetNextStepsSummary,
  } = useGetNextStepsSummary(listingAlias);

  const { sections: sectionList = [{ ...MOCK_SECTION, title: 'Next steps' }, MOCK_SECTION] } = data || {};
  const isNotLoadingAndSectionListEmpty = isFetching === false && isPending === false && sectionList.length == 0;

  // we need to have a border to the next steps module if we can access the rental protection banner
  const moduleCardSmBorderTop = isRentalProtectionEligible ? 'unset' : 'none';

  // TODO: remove useMemo for other active path conditional checks are made
  /**
   * when using the tabs component, all tab child components will render
   * only do things if we certain paths are active
   */
  const isRelevantActivePath = activeDashboardPath === RMW_PATH.DASHBOARD_OVERVIEW;

  useEffect(() => {
    if (!isRelevantActivePath) {
      return;
    }

    if (listingAlias) {
      // initial render - no cached data yet
      if (sectionList.length === 0 || isPending) {
        fetchNextStepsSummary();
        return;
      }

      if (isStale) {
        fetchNextStepsSummary();
      }
    }
  }, [isRelevantActivePath, listingAlias]);

  // clicked save cta
  useEffect(() => {
    if (!isRelevantActivePath) {
      return;
    }

    if (initialRender.current === true) {
      initialRender.current = false;
      return;
    }

    setIsSaving(isSaveComplete);

    if (isSaveComplete === false && hasFieldError === false && hasServerError() === false) {
      setDisplayApplicationsModal(false);
      fetchNextStepsSummary();
    }
  }, [isSaveComplete]);

  // server error
  useEffect(() => {
    if (!isRelevantActivePath) {
      return;
    }

    if (hasServerError()) {
      setIsSaving(false);

      // fixes issue where saving on blur happens
      // this will keep the manual saving functionality in review edit modal
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
    }
  }, [isSaveComplete, serverErrorObj?.fieldName]);

  // payment gateway response
  useEffect(() => {
    if (!isRelevantActivePath) {
      return;
    }

    if (isResponseSuccess) {
      fetchNextStepsSummary();
      queryClient.invalidateQueries({ queryKey: ['getPropertyDetailsOverview', listingAlias] });
    }
  }, [isResponseSuccess]);

  const fetchNextStepsSummary = () => {
    refetchGetNextStepsSummary();
  };

  const clearServerError = () => {
    if (serverErrorObj?.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const handleApplicationsModalClose = () => {
    setDisplayApplicationsModal(false);
  };

  const handleApplicationsModalOpen = () => {
    clearServerError();
    updateToModularListingDefaultState();
  };

  const updateToModularListingDefaultState = () => {
    actions.updateAnyProperty({
      reducerDispatch,
      data: {
        isInModalState: true,
        isSaveComplete: false,
        shouldSaveOnBlur: false,
      },
    });
  };

  const hasServerError = () => {
    return serverErrorObj?.fieldName !== '';
  };

  const onSaveClick = () => {
    // communicate that cta has been clicked
    actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: true });
  };

  const updateDisplayPaymentGateway = (value: boolean) => {
    pgActions.updateAllData({
      reducerDispatch: pgReducerDispatch,
      data: {
        displayPaymentGateway: value,
        displayContext: DISPLAY_CONTEXT.NEXT_STEP,
      },
    });
  };

  const updateSelectedProductType = (selectedProductType: number) => {
    dispatch(
      PropertiesActions.updateCurrentProperty({
        selectedProductType,
      }),
    );
  };

  const handleTileClick = ({ sectionTitle, id, url }: { sectionTitle: string; id: string; url: string }) => {
    const triggerObject = sectionTitle.toLowerCase().replace(/\s/g, '_');
    const triggerSource = id
      .split(/(?=[A-Z])/)
      .map((substring) => substring.toLowerCase())
      .join('_');

    NextStepsRecommendationsCSEvents.ClickNextStepsRecommendations({
      user,
      currentProperty,
      triggerSource,
      triggerObject,
    });

    if (!url) {
      if (id === TILE_ID.ENABLE_APPLICATIONS) {
        ApplicationsCSEvents.ClickEditAcceptApplications({
          user,
          currentProperty,
          triggerLocation: TRIGGER_LOCATIONS.NEXT_STEPS_APPS,
        });
        setDisplayApplicationsModal(true);
      }

      if (id === TILE_ID.UPGRADE_TO_PREMIUM) {
        updateSelectedProductType(PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM);
        updateDisplayPaymentGateway(true);

        NextStepsRecommendationsCSEvents.ClickUpgradeToPremiumFromRecActions({
          user,
          currentProperty,
        });
      }

      return;
    }

    if (id === TILE_ID.RENTAL_PROTECTION || id === TILE_ID.RELIST) {
      window.router.transitionTo(url);

      return;
    }

    window.router.updateLocation(url, '_self');
  };

  const renderEmptySectionList = () => {
    return (
      <Box>
        <SimpleCard
          bodyHeading={'Nice work!'}
          body={'Your property looks good. Check back here for future actions you can take to manage your rental.'}
          withChevron={false}
        />
      </Box>
    );
  };

  const renderSectionList = ({ title, tiles }: { title: string; tiles: Array<TileType> }, index: number) => {
    const titleFontType = index === 0 ? 'body-bold' : 'body-sm-bold';
    const titleFontColor = index === 0 ? 'text.neutral' : 'text.subtle';
    const titleMarginBottom = index === 0 ? '400' : '200';

    return (
      <Section
        title={title}
        tileList={tiles}
        titleFontColor={titleFontColor}
        titleFontType={titleFontType}
        titleMarginBottom={titleMarginBottom}
        tileFontType="body-xs-bold"
        tileSubtitleFontType="body-xs"
        onTileClick={handleTileClick}
        isLoading={isPending}
        key={`Section-${index}`}
      />
    );
  };

  // TODO: check mobile web view - display border-top for sm breakpoint for rental protection listings
  return (
    <div data-testid="NextStepsModule">
      <ConnectedModuleCard
        title={isNotLoadingAndSectionListEmpty ? 'Next steps' : null}
        ctaFontColor="text.subtle"
        ctaFontType="body-sm"
        enableHover={false}
        justifyHeadingContent={'center'}
        mediaQuery={{
          sm: { borderTop: moduleCardSmBorderTop, borderLeft: 'none', borderRight: 'none' },
          md: { borderTop: 'unset', borderLeft: 'none', borderRight: 'none' },
          xl: { borderTop: 'unset', borderLeft: 'unset', borderRight: 'unset' },
        }}
      >
        {error ? (
          <ErrorMessagesAlert
            data-testid="PaymentsModuleError"
            errorMessages={
              error.error.data.partialMessages || [{ text: error.error.message || 'Sorry, an error occurred.' }]
            }
          />
        ) : (
          <>{isNotLoadingAndSectionListEmpty ? renderEmptySectionList() : sectionList.map(renderSectionList)}</>
        )}
      </ConnectedModuleCard>

      {!isFetching && displayApplicationsModal && (
        <ApplicationsModalWrapper
          onApplicationsModalClose={handleApplicationsModalClose}
          onApplicationsModalOpen={handleApplicationsModalOpen}
          onSaveClick={onSaveClick}
          isSaving={isSaving}
        />
      )}
    </div>
  );
};

export default memo(NextStepsModule);
